// AssignmentDialog — Schrank Mitarbeiter/Abteilung zuweisen + Historie
import React, { useState, useEffect, useMemo } from 'react';
import {
  UserPlus, X, Loader2, History, Building2, User as UserIcon, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { schrankApi, usersApi, wacheApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import { cn } from '../../lib/utils';

export function AssignmentDialog({ open, onOpenChange, schrank, canManage, onChanged }) {
  const [tab, setTab] = useState('mitarbeiter');
  const [users, setUsers] = useState([]);
  const [abteilungen, setAbteilungen] = useState([]);
  const [search, setSearch] = useState('');
  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [notiz, setNotiz] = useState('');

  // Load users + abteilungen once
  useEffect(() => {
    if (!open) return;
    const loadLists = async () => {
      try {
        const [usersRes, abtRes] = await Promise.all([
          usersApi.getAll({ ist_aktiv: true }),
          wacheApi.getAbteilungen(),
        ]);
        setUsers(usersRes.data || []);
        setAbteilungen(abtRes.data || []);
      } catch (e) {
        console.error(e);
      }
    };
    loadLists();
  }, [open]);

  // Load history
  useEffect(() => {
    if (!open || !schrank?.id) return;
    const load = async () => {
      setIsLoadingHistory(true);
      try {
        const res = await schrankApi.listAssignments({ schrank_id: schrank.id });
        setHistory(res.data || []);
      } finally {
        setIsLoadingHistory(false);
      }
    };
    load();
    setSearch('');
    setNotiz('');
  }, [open, schrank?.id]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users.slice(0, 20);
    return users.filter(u => {
      const name = `${u.vorname || ''} ${u.nachname || ''}`.toLowerCase();
      const abt = abteilungen.find(a => a.id === u.abteilung_id)?.name?.toLowerCase() || '';
      return name.includes(q) || abt.includes(q) || (u.loginname || '').toLowerCase().includes(q);
    }).slice(0, 30);
  }, [users, abteilungen, search]);

  const filteredAbteilungen = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return abteilungen;
    return abteilungen.filter(a => (a.name || '').toLowerCase().includes(q));
  }, [abteilungen, search]);

  const handleAssignMitarbeiter = async (userId) => {
    setIsSaving(true);
    try {
      await schrankApi.assign(schrank.id, {
        mitarbeiter_id: userId,
        notiz: notiz.trim() || null,
      });
      toast.success('Schrank zugewiesen');
      onChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Zuweisung fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAssignAbteilung = async (abteilungId) => {
    setIsSaving(true);
    try {
      await schrankApi.assign(schrank.id, {
        abteilung_id: abteilungId,
        notiz: notiz.trim() || null,
      });
      toast.success('Schrank zugewiesen');
      onChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Zuweisung fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEndAssignment = async () => {
    if (!schrank.aktuelle_assignment_id) return;
    setIsSaving(true);
    try {
      await schrankApi.endAssignment(schrank.aktuelle_assignment_id);
      toast.success('Zuweisung beendet');
      onChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Beenden fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  const abteilungName = (id) =>
    abteilungen.find(a => a.id === id)?.name || '—';

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (!schrank) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Schrank {schrank.name}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline">{schrank.typ}{schrank.custom_typ ? `: ${schrank.custom_typ}` : ''}</Badge>
            {schrank.mitarbeiter_id ? (
              <Badge className="bg-orange-500">Belegt: {schrank.mitarbeiter_name}</Badge>
            ) : schrank.abteilung_id ? (
              <Badge className="bg-orange-500">Belegt: {schrank.abteilung_name}</Badge>
            ) : (
              <Badge className="bg-emerald-600">Frei</Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        {schrank.aktuelle_assignment_id && canManage && (
          <div className="border rounded-lg p-3 bg-orange-50 dark:bg-orange-950/20 flex items-center justify-between gap-3">
            <div className="text-sm">
              <p className="font-medium">Aktuelle Zuweisung</p>
              <p className="text-muted-foreground">
                {schrank.mitarbeiter_name || schrank.abteilung_name}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleEndAssignment}
              disabled={isSaving}
              className="text-destructive hover:text-destructive"
              data-testid="end-assignment-btn"
            >
              <X className="w-4 h-4 mr-1" />
              Zuweisung beenden
            </Button>
          </div>
        )}

        {canManage && (
          <div>
            <Label htmlFor="assign-notiz">Notiz (optional)</Label>
            <Input
              id="assign-notiz"
              value={notiz}
              onChange={(e) => setNotiz(e.target.value)}
              placeholder="z.B. 'Schlüssel #42 übergeben'"
            />
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="mitarbeiter" disabled={!canManage}>
              <UserIcon className="w-4 h-4 mr-1" /> Mitarbeiter
            </TabsTrigger>
            <TabsTrigger value="abteilung" disabled={!canManage}>
              <Building2 className="w-4 h-4 mr-1" /> Abteilung
            </TabsTrigger>
            <TabsTrigger value="history">
              <History className="w-4 h-4 mr-1" /> Historie
            </TabsTrigger>
          </TabsList>

          <TabsContent value="mitarbeiter" className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Mitarbeiter oder Abteilung suchen..."
                className="pl-9"
                data-testid="assign-search-input"
              />
            </div>
            <ScrollArea className="h-72 border rounded-md">
              {filteredUsers.length === 0 ? (
                <p className="p-6 text-center text-sm text-muted-foreground">
                  Keine Mitarbeiter gefunden.
                </p>
              ) : filteredUsers.map((u) => (
                <button
                  key={u.id}
                  onClick={() => handleAssignMitarbeiter(u.id)}
                  disabled={isSaving}
                  className={cn(
                    'w-full p-2.5 text-left flex items-center gap-3 hover:bg-muted/50 transition-colors border-b last:border-b-0',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                  data-testid={`assign-user-${u.id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm shrink-0">
                    {u.vorname?.[0]}{u.nachname?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">
                      {u.vorname} {u.nachname}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {abteilungName(u.abteilung_id)}
                      {u.dienstgrad && ` · ${u.dienstgrad}`}
                    </p>
                  </div>
                  <UserPlus className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
              ))}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="abteilung" className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Abteilung suchen..."
                className="pl-9"
              />
            </div>
            <ScrollArea className="h-72 border rounded-md">
              {filteredAbteilungen.length === 0 ? (
                <p className="p-6 text-center text-sm text-muted-foreground">
                  Keine Abteilungen.
                </p>
              ) : filteredAbteilungen.map((a) => (
                <button
                  key={a.id}
                  onClick={() => handleAssignAbteilung(a.id)}
                  disabled={isSaving}
                  className="w-full p-2.5 text-left flex items-center gap-3 hover:bg-muted/50 transition-colors border-b last:border-b-0 disabled:opacity-50"
                  data-testid={`assign-abt-${a.id}`}
                >
                  <Building2 className="w-5 h-5 text-muted-foreground shrink-0" />
                  <p className="font-medium text-sm flex-1 truncate">{a.name}</p>
                  <UserPlus className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
              ))}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="history">
            <ScrollArea className="h-72 border rounded-md">
              {isLoadingHistory ? (
                <div className="p-6 text-center">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </div>
              ) : history.length === 0 ? (
                <p className="p-6 text-center text-sm text-muted-foreground">
                  Noch keine Zuweisungen für diesen Schrank.
                </p>
              ) : (
                <div className="divide-y">
                  {history.map((h) => (
                    <div key={h.id} className="p-2.5" data-testid={`history-${h.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={h.status === 'AKTIV' ? 'default' : 'secondary'}
                               className={h.status === 'AKTIV' ? 'bg-orange-500' : ''}>
                          {h.status === 'AKTIV' ? 'AKTIV' : 'Beendet'}
                        </Badge>
                        <span className="font-medium text-sm">
                          {h.mitarbeiter_name || h.abteilung_name || '—'}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Zugewiesen: {formatDate(h.zugewiesen_am)} von {h.zugewiesen_von_name}
                      </p>
                      {h.beendet_am && (
                        <p className="text-xs text-muted-foreground">
                          Beendet: {formatDate(h.beendet_am)} von {h.beendet_von_name}
                        </p>
                      )}
                      {h.notiz && (
                        <p className="text-xs italic mt-1">„{h.notiz}“</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AssignmentDialog;
